import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Edit,
  Delete,
  Visibility,
  Add,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { apiService } from '../services/api';
import { TimeEntry, Project } from '../types';

export const TimeEntriesList: React.FC = () => {
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    entry: TimeEntry | null;
  }>({ open: false, entry: null });
  const [screenshotDialog, setScreenshotDialog] = useState<{
    open: boolean;
    screenshotRef: string | null;
  }>({ open: false, screenshotRef: null });
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(7, 'day'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  useEffect(() => {
    loadData();
  }, [startDate, endDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [entriesData, projectsData] = await Promise.all([
        apiService.getTimeEntries(),
        apiService.getProjects(),
      ]);
      setEntries(entriesData);
      setProjects(projectsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditEntry = (entry: TimeEntry) => {
    setEditDialog({ open: true, entry });
  };

  const handleSaveEntry = async () => {
    if (!editDialog.entry) return;

    try {
      await apiService.updateTimeEntry(editDialog.entry.id, editDialog.entry);
      setEditDialog({ open: false, entry: null });
      loadData();
    } catch (error) {
      console.error('Failed to update entry:', error);
    }
  };

  const handleDeleteEntry = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      try {
        await apiService.deleteTimeEntry(id);
        loadData();
      } catch (error) {
        console.error('Failed to delete entry:', error);
      }
    }
  };

  const handleViewScreenshot = (screenshotRef: string) => {
    setScreenshotDialog({ open: true, screenshotRef });
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDateTime = (dateString: string) => {
    return dayjs(dateString).format('MMM DD, YYYY HH:mm');
  };

  const getProjectName = (projectId: string | null) => {
    if (!projectId) {
      return 'Unassigned';
    }
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'Unknown Project';
  };

  if (loading) {
    return <Typography>Loading time entries...</Typography>;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Time Entries</Typography>
          <Box display="flex" gap={2} alignItems="center">
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={setStartDate}
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={setEndDate}
              slotProps={{ textField: { size: 'small' } }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setEditDialog({ open: true, entry: null })}
            >
              Add Manual Entry
            </Button>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>AI Description</TableCell>
                <TableCell>Screenshot</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{formatDateTime(entry.startTime)}</TableCell>
                  <TableCell>
                    <Chip
                      label={getProjectName(entry.projectId)}
                      size="small"
                      color={entry.projectId ? "primary" : "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{formatDuration(entry.duration)}</TableCell>
                  <TableCell>
                    {entry.aiDescription ? (
                      <Tooltip title={entry.aiDescription}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {entry.aiDescription}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Manual entry
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {entry.screenshotRef ? (
                      <IconButton
                        size="small"
                        onClick={() => handleViewScreenshot(entry.screenshotRef!)}
                      >
                        <Visibility />
                      </IconButton>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        None
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleEditEntry(entry)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteEntry(entry.id)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="textSecondary">
                      No time entries found for the selected date range
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Edit Dialog */}
        <Dialog
          open={editDialog.open}
          onClose={() => setEditDialog({ open: false, entry: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editDialog.entry?.id ? 'Edit Time Entry' : 'Add Manual Entry'}
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <FormControl fullWidth>
                <InputLabel>Project</InputLabel>
                <Select
                  value={editDialog.entry?.projectId || ''}
                  onChange={(e) =>
                    setEditDialog({
                      ...editDialog,
                      entry: editDialog.entry
                        ? { ...editDialog.entry, projectId: e.target.value || null }
                        : null,
                    })
                  }
                >
                  <MenuItem value="">
                    <em>Unassigned</em>
                  </MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Duration (minutes)"
                type="number"
                value={editDialog.entry?.duration || ''}
                onChange={(e) =>
                  setEditDialog({
                    ...editDialog,
                    entry: editDialog.entry
                      ? { ...editDialog.entry, duration: parseInt(e.target.value) }
                      : null,
                  })
                }
              />
              <TextField
                label="Description"
                multiline
                rows={3}
                value={editDialog.entry?.aiDescription || ''}
                onChange={(e) =>
                  setEditDialog({
                    ...editDialog,
                    entry: editDialog.entry
                      ? { ...editDialog.entry, aiDescription: e.target.value }
                      : null,
                  })
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog({ open: false, entry: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveEntry} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Screenshot Dialog */}
        <Dialog
          open={screenshotDialog.open}
          onClose={() => setScreenshotDialog({ open: false, screenshotRef: null })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Screenshot</DialogTitle>
          <DialogContent>
            {screenshotDialog.screenshotRef && (
              <img
                src={`/api/screenshots/${screenshotDialog.screenshotRef}`}
                alt="Screenshot"
                style={{ width: '100%', height: 'auto' }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setScreenshotDialog({ open: false, screenshotRef: null })}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </LocalizationProvider>
  );
};
